import React, { useState } from "react";
import { toast } from "react-toastify";
import { Icon } from "@iconify/react";
import { AgGridReact } from "ag-grid-react";
import { ColDef } from "ag-grid-community";
import { formatDisplayDate } from "../../../utils/date";
import {
  ButtonPrimary,
  ButtonSecondary,
  HStack,
  VStack,
} from "../../../components/utils";
import { FileDropzone } from "../../../components/shared/FileDropzone";
import {
  downloadCustomEmailExcel,
  parseCustomEmailExcel,
  validateCustomEmailExcel,
} from "../../../api/customEmail";
import { ParsedRow } from "./types";

interface Props {
  customVars: string[];
  initialRows?: ParsedRow[];
  onNext: (rows: ParsedRow[]) => void;
  onBack: () => void;
}

export default function Step4Upload({
  customVars,
  initialRows,
  onNext,
  onBack,
}: Props) {
  const [file, setFile] = useState<File | null>(null);
  const [validating, setValidating] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [downloading, setDownloading] = useState(false);
  const [parsedRows, setParsedRows] = useState<ParsedRow[] | null>(
    initialRows && initialRows.length > 0 ? initialRows : null
  );

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await downloadCustomEmailExcel(customVars);
    } catch {
      toast.error("Failed to download template");
    } finally {
      setDownloading(false);
    }
  };

  const handleFileSelect = async (f: File) => {
    setFile(f);
    setErrors([]);
    setParsedRows(null);
    setValidating(true);
    try {
      const result = await validateCustomEmailExcel(f, customVars);
      if (!result.valid) {
        setErrors(result.errors);
      } else {
        // Auto-parse on successful validation to show preview table
        const rows = await parseCustomEmailExcel(f, customVars);
        setParsedRows(rows);
      }
    } catch {
      setErrors(["Failed to validate file. Please check the format."]);
    } finally {
      setValidating(false);
    }
  };

  const handleContinue = () => {
    if (errors.length > 0) {
      toast.error("Please fix errors in the uploaded file first");
      return;
    }
    if (!parsedRows || parsedRows.length === 0) {
      toast.error("Please upload the filled Excel file first");
      return;
    }
    onNext(parsedRows);
  };

  const hasData = parsedRows && parsedRows.length > 0;

  return (
    <VStack className="gap-6 p-6 h-full">
      {/* Show upload steps only when no data loaded yet */}
      {!hasData && (
        <>
          <VStack className="gap-2">
            <HStack className="items-center justify-between">
              <p className="text-sm font-medium text-gray-700">
                Step 1: Download the template Excel with all your employees
                pre-filled.
              </p>
              <ButtonPrimary
                className="flex items-center gap-1 whitespace-nowrap"
                onClick={handleDownload}
                loading={downloading}
              >
                <Icon icon="mdi:download" height={16} />
                Download Employee Template Excel
              </ButtonPrimary>
            </HStack>
            <p className="text-xs text-gray-500">
              The file has columns: <span className="font-medium">HR ID</span>
              {customVars.map((v) => (
                <span key={v} className="ml-1 font-medium text-orange-700">
                  , {v.replace(/#/g, "")}
                </span>
              ))}
              . Select HR IDs from the dropdown
              {customVars.length > 0
                ? " and fill in the custom variable columns"
                : ""}
              , then save and upload.
            </p>
          </VStack>

          <VStack className="gap-2">
            <p className="text-sm font-medium text-gray-700">
              Step 2: Upload the filled Excel file.
            </p>
            <FileDropzone
              onFileSelect={handleFileSelect}
              onClear={() => {
                setFile(null);
                setErrors([]);
                setParsedRows(null);
              }}
              selectedFile={file ?? undefined}
              acceptedFileExtensions={[".xlsx", ".xls"]}
              instructionText=".xlsx or .xls files only"
              disabled={validating}
            />
            {validating && (
              <p className="text-xs text-blue-600 animate-pulse">
                Validating file...
              </p>
            )}
            {errors.length > 0 && (
              <VStack className="gap-1 p-3 bg-red-50 border border-red-200 rounded">
                <p className="text-xs font-semibold text-red-700">
                  {errors.length} validation error(s):
                </p>
                <ul className="list-disc list-inside">
                  {errors.slice(0, 10).map((err, i) => (
                    <li key={i} className="text-xs text-red-600">
                      {err}
                    </li>
                  ))}
                  {errors.length > 10 && (
                    <li className="text-xs text-red-500 italic">
                      ...and {errors.length - 10} more
                    </li>
                  )}
                </ul>
              </VStack>
            )}
          </VStack>
        </>
      )}

      {/* Parsed data preview table */}
      {parsedRows && parsedRows.length > 0 && (
        <VStack className="gap-2">
          <HStack className="items-center justify-between">
            <p className="text-sm font-medium text-gray-700">
              Preview ({parsedRows.length} employee
              {parsedRows.length !== 1 ? "s" : ""})
            </p>
            <ButtonSecondary
              className="flex items-center gap-1 text-xs"
              onClick={() => {
                setFile(null);
                setErrors([]);
                setParsedRows(null);
              }}
            >
              <Icon icon="mdi:refresh" height={14} />
              Re-upload
            </ButtonSecondary>
          </HStack>
          <div
            className="ag-theme-material"
            style={{
              height: `${Math.min(parsedRows.length + 1, 10) * 48 + 56}px`,
            }}
          >
            <AgGridReact
              rowData={parsedRows}
              columnDefs={[
                { field: "employeeHrId", headerName: "HR ID", minWidth: 100 },
                { field: "employeeName", headerName: "Name", minWidth: 160 },
                {
                  field: "employeeEmail",
                  headerName: "Email",
                  minWidth: 200,
                  flex: 1,
                },
                ...customVars.map((v) => {
                  const key = v.replace(/#/g, "");
                  return {
                    headerName: key,
                    valueGetter: (p: any) => p.data?.customVars?.[key] ?? "",
                    valueFormatter: (p: any) => {
                      const val = p.value as string;
                      if (!val) return "";
                      // Detect ISO date strings (YYYY-MM-DD)
                      if (/^\d{4}-\d{2}-\d{2}$/.test(val)) {
                        const formatted = formatDisplayDate(val);
                        return formatted === "Invalid Date" ? val : formatted;
                      }
                      return val;
                    },
                    minWidth: 130,
                  } as ColDef;
                }),
              ]}
              defaultColDef={{ resizable: true, sortable: true }}
              onGridReady={(p) => p.api.sizeColumnsToFit()}
            />
          </div>
        </VStack>
      )}

      <HStack className="flex-row-reverse gap-3 mt-auto">
        <ButtonPrimary
          onClick={handleContinue}
          disabled={validating || (!file && !parsedRows) || errors.length > 0}
        >
          Continue
        </ButtonPrimary>
        <ButtonSecondary onClick={onBack}>Back</ButtonSecondary>
      </HStack>
    </VStack>
  );
}
